import { create } from 'zustand';

export type CalendarViewMode = 'day' | '3day' | 'week' | '2week' | 'month';

const LS_PREFIX = 'calendarUI:';
const LS_KEYS = {
  viewMode: `${LS_PREFIX}viewMode`,
  selectedCalendars: `${LS_PREFIX}selectedCalendars`,
  filterByProject: `${LS_PREFIX}filterByProject`,
  filterByArea: `${LS_PREFIX}filterByArea`,
  showCompleted: `${LS_PREFIX}showCompleted`,
  showTasks: `${LS_PREFIX}showTasks`,
  includeGoogleCalendar: `${LS_PREFIX}includeGoogleCalendar`,
} as const;

function readJSON<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    return raw === null ? fallback : (JSON.parse(raw) as T);
  } catch {
    return fallback;
  }
}

function writeJSON(key: string, value: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // ignore quota / privacy-mode failures
  }
}

interface CalendarUIState {
  viewMode: CalendarViewMode;
  setViewMode: (mode: CalendarViewMode) => void;

  // currentDate is intentionally NOT persisted — always start at "now"
  currentDate: Date;
  setCurrentDate: (date: Date) => void;
  goToToday: () => void;

  selectedCalendars: string[]; // [] = all
  setSelectedCalendars: (ids: string[]) => void;

  filterByProject: string | undefined;
  setFilterByProject: (id: string | undefined) => void;

  filterByArea: string | undefined;
  setFilterByArea: (id: string | undefined) => void;

  showCompleted: boolean;
  setShowCompleted: (v: boolean) => void;

  showTasks: boolean;
  setShowTasks: (v: boolean) => void;

  includeGoogleCalendar: boolean;
  setIncludeGoogleCalendar: (v: boolean) => void;
}

export const useCalendarUIStore = create<CalendarUIState>((set) => ({
  viewMode: readJSON<CalendarViewMode>(LS_KEYS.viewMode, 'week'),
  setViewMode: (mode) => {
    writeJSON(LS_KEYS.viewMode, mode);
    set({ viewMode: mode });
  },

  currentDate: new Date(),
  setCurrentDate: (date) => set({ currentDate: date }),
  goToToday: () => set({ currentDate: new Date() }),

  selectedCalendars: readJSON<string[]>(LS_KEYS.selectedCalendars, []),
  setSelectedCalendars: (ids) => {
    writeJSON(LS_KEYS.selectedCalendars, ids);
    set({ selectedCalendars: ids });
  },

  filterByProject: readJSON<string | undefined>(LS_KEYS.filterByProject, undefined),
  setFilterByProject: (id) => {
    writeJSON(LS_KEYS.filterByProject, id ?? null);
    set({ filterByProject: id });
  },

  filterByArea: readJSON<string | undefined>(LS_KEYS.filterByArea, undefined),
  setFilterByArea: (id) => {
    writeJSON(LS_KEYS.filterByArea, id ?? null);
    set({ filterByArea: id });
  },

  showCompleted: readJSON<boolean>(LS_KEYS.showCompleted, false),
  setShowCompleted: (v) => {
    writeJSON(LS_KEYS.showCompleted, v);
    set({ showCompleted: v });
  },

  showTasks: readJSON<boolean>(LS_KEYS.showTasks, true),
  setShowTasks: (v) => {
    writeJSON(LS_KEYS.showTasks, v);
    set({ showTasks: v });
  },

  includeGoogleCalendar: readJSON<boolean>(LS_KEYS.includeGoogleCalendar, true),
  setIncludeGoogleCalendar: (v) => {
    writeJSON(LS_KEYS.includeGoogleCalendar, v);
    set({ includeGoogleCalendar: v });
  },
}));
